import React, { useEffect, useMemo, useState } from "react";
import SectionHeader from "../SectionHeader";
import { Loader2 } from "lucide-react";
import { http } from "../../axios/axios";
import ReviewCard from "../ReviewCard";
import {
  ArrowRight,
  MessageSquareText,
  Star,
  ExternalLink,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

export const HomeReviewSection = () => {
  const [featuredReview, setFeaturedReview] = useState([]);

  const [googleReviewInfo, setGoogleReviewInfo] = useState(null);

  const [loading, setLoading] = useState(true);

  const REVIEWS_PER_SLIDE = 3;

 /*  const fetchReviews = async () => {
    try {
      setLoading(true);

      const googleRes = await http.get("/reviews/google");

      // console.log("GOOGLE REVIEWS RESPONSE:", googleRes.data);

      const googleReviews = googleRes.data?.data || [];

      setGoogleReviewInfo({
        rating: googleRes.data?.averageRating || 0,

        total: googleRes.data?.totalReviewCount || 0,

        reviewsUrl: googleRes.data?.reviewsUrl || "",

        writeReviewUrl: googleRes.data?.writeReviewUrl || "",
      });

      if (googleReviews.length > 0) {
        setFeaturedReview(googleReviews);
        return;
      }

      const localRes = await http.get("/reviews/featured");

      setFeaturedReview(localRes.data?.data || []);
    } catch (error) {
      console.error(
        "Review fetch error:",
        error.response?.data || error.message,
      );

      try {
        const localRes = await http.get("/reviews/featured");

        setFeaturedReview(localRes.data?.data || []);
      } catch (localError) {
        console.error(
          "Local review error:",
          localError.response?.data || localError.message,
        );

        setFeaturedReview([]);
      }
    } finally {
      setLoading(false);
    }
  };
 */
  
  const fetchReviews = async () => {
  setLoading(true);

  try {
    // 1. Always load review cards from MongoDB
    try {
      const localRes = await http.get("/reviews/featured");

      setFeaturedReview(
        localRes.data?.data || []
      );
    } catch (localError) {
      console.error(
        "Local review error:",
        localError.response?.data ||
          localError.message
      );

      setFeaturedReview([]);
    }

    // 2. Google is only for rating/count/links
    try {
      const googleRes = await http.get(
        "/reviews/google"
      );

      setGoogleReviewInfo({
        rating:
          googleRes.data?.averageRating || 0,

        total:
          googleRes.data?.totalReviewCount || 0,

        reviewsUrl:
          googleRes.data?.reviewsUrl || "",

        writeReviewUrl:
          googleRes.data?.writeReviewUrl || "",
      });
    } catch (googleError) {
      console.error(
        "Google review error:",
        googleError.response?.data ||
          googleError.message
      );

      // Google failure should NOT break cards
      setGoogleReviewInfo(null);
    }
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchReviews();
  }, []);

  const reviewSlides = useMemo(() => {
    const slides = [];

    for (let i = 0; i < featuredReview.length; i += REVIEWS_PER_SLIDE) {
      slides.push(featuredReview.slice(i, i + REVIEWS_PER_SLIDE));
    }

    return slides;
  }, [featuredReview]);

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        badge="Customer Reviews"
        title="What Our Clients Say"
        description={
          <span className="lg:whitespace-nowrap">
            We're proud to have earned the trust of homeowners and businesses through quality security solutions and reliable service.
          </span>
        }
        showLine
        secondAction={
          googleReviewInfo?.reviewsUrl && (
            <a
              href={googleReviewInfo.reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="
          group
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-xl
          border
          border-slate-200
          bg-white
          px-4
          py-2.5
          text-sm
          font-semibold
          text-slate-700
          shadow-sm
          transition-all
          duration-300
          hover:-translate-y-0.5
          hover:border-blue-200
          hover:bg-blue-50
          hover:text-blue-700
          hover:shadow-md
        "
            >
              <Star
                size={17}
                className="
            fill-amber-400
            text-amber-400
            transition-transform
            duration-300
            group-hover:scale-110
          "
              />

              <span>View All Reviews</span>

              <ExternalLink
                size={14}
                className="
            opacity-60
            transition-all
            duration-300
            group-hover:translate-x-0.5
            group-hover:-translate-y-0.5
            group-hover:opacity-100
          "
              />
            </a>
          )
        }
        action={
          googleReviewInfo?.writeReviewUrl && (
            <a
              href={googleReviewInfo.writeReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="
          group
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-xl
          bg-gradient-to-r
          from-blue-600
          to-blue-700
          px-5
          py-2.5
          text-sm
          font-semibold
          text-white
          shadow-lg
          shadow-blue-600/20
          transition-all
          duration-300
          hover:-translate-y-0.5
          hover:from-blue-700
          hover:to-blue-800
          hover:shadow-xl
          hover:shadow-blue-600/30
          active:translate-y-0
        "
            >
              <span
                className="
            flex
            h-7
            w-7
            items-center
            justify-center
            rounded-lg
            bg-white/15
            transition
            group-hover:bg-white/20
          "
              >
                <MessageSquareText size={16} />
              </span>

              <span>Write a Review</span>

              <ArrowRight
                size={15}
                className="
            transition-transform
            duration-300
            group-hover:translate-x-1
          "
              />
            </a>
          )
        }
      />

      {/* Google rating summary */}
      {googleReviewInfo && (
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <div className="text-xl text-amber-400">★★★★★</div>

          <div>
            <span className="font-semibold text-slate-900">
              {googleReviewInfo.rating}
            </span>

            <span className="ml-2 text-sm text-slate-500">
              based on {googleReviewInfo.total} Google reviews
            </span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 size={36} className="animate-spin text-blue-600" />
        </div>
      ) : reviewSlides.length > 0 ? (
        /*
          Review slider
        */
        <Swiper
          modules={[Autoplay, Pagination]}
          slidesPerView={1}
          spaceBetween={30}
          speed={800}
          loop={reviewSlides.length > 1}
          autoHeight
          observer
          observeParents
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
          }}
          className="mt-12 pb-12"
        >
          {reviewSlides.map((slideReviews, slideIndex) => (
            <SwiperSlide key={slideIndex}>
              <div
                className="
                    grid
                    items-start
                    gap-8
                    grid-cols-1
                    md:grid-cols-2
                    lg:grid-cols-3
                  "
              >
                {slideReviews.map((review) => (
                  <ReviewCard
                    key={review._id}
                    review={review}
                  />
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="mt-12 py-14 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
          <p className="text-slate-600">
            No individual reviews are currently available.
          </p>
        </div>
      )}
    </section>
  );
};
